import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as ReactRouterLink } from 'react-router-dom';
import {
    Box,
    VStack,
    HStack,
    Heading,
    Text,
    Button,
    Badge,
    Divider,
    Alert,
    AlertIcon,
    Input,
    Textarea,
    FormControl,
    FormLabel,
    FormHelperText,
    useDisclosure,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, ViewIcon } from '@chakra-ui/icons';
import { useToast } from '../hooks/useToast';
import { useConfirmDialog } from './shared/ConfirmDialog';
import getCSRFtoken from '../utils/getCSRFtoken';
import fetchWithAuth from '../utils/fetchWithAuth';
import LoadingSpinner from './shared/LoadingSpinner';
import PageHeader from './shared/PageHeader';
import SurveyPageDesigner from './SurveyPageDesigner';
import PreflightModal from './PreflightModal';
import { getStateBadgeColor } from '../theme/designSystem';

/**
 * PollSettings - Manage poll state, staging, and advanced options
 *
 * Separated from PollCreator to keep question editing focused.
 */
function PollSettings() {
    const { pollId } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { confirm, ConfirmDialogComponent } = useConfirmDialog();
    const csrfToken = getCSRFtoken();

    const [poll, setPoll] = useState(null);
    const [questionCount, setQuestionCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [prologue, setPrologue] = useState('');
    const [prologueEs, setPrologueEs] = useState('');
    const [publicTitle, setPublicTitle] = useState('');
    const [publicTitleEs, setPublicTitleEs] = useState('');
    const [savingPrologue, setSavingPrologue] = useState(false);

    useEffect(() => {
        loadPoll();
    }, [pollId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadPoll = async () => {
        try {
            setLoading(true);

            const pollResponse = await fetchWithAuth(`/api/polls/${pollId}/`);
            const pollData = await pollResponse.json();
            setPoll(pollData);
            setPrologue(pollData.prologue || '');
            setPrologueEs(pollData.prologue_es || '');
            setPublicTitle(pollData.public_title || '');
            setPublicTitleEs(pollData.public_title_es || '');

            const questionsResponse = await fetchWithAuth(`/api/questions/?poll=${pollId}`);
            const questionsData = await questionsResponse.json();
            setQuestionCount((questionsData.results || []).length);

        } catch (error) {
            toast.showError('Error', 'Failed to load poll');
        } finally {
            setLoading(false);
        }
    };

    const { isOpen: isPreflightOpen, onOpen: onPreflightOpen, onClose: onPreflightClose } = useDisclosure();

    const handleSetPollLive = () => {
        onPreflightOpen();
    };

    const handleCompletePoll = () => {
        confirm({
            title: 'Complete Poll',
            message: `Are you sure you want to complete "${poll.title}"? This will close the poll and prevent further responses.`,
            confirmText: 'Complete Poll',
            onConfirm: async () => {
                try {
                    const response = await fetchWithAuth(`/api/polls/${pollId}/complete/`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRFToken': csrfToken,
                        },
                    });

                    if (!response.ok) throw new Error('Failed to complete poll');

                    const data = await response.json();
                    setPoll(data.poll);
                    toast.showSuccess('Success', 'Poll has been completed');
                } catch (error) {
                    toast.showError('Error', 'Failed to complete poll');
                }
            }
        });
    };

    const handleDeletePoll = () => {
        confirm({
            title: 'Delete Poll',
            message: 'Are you sure you want to delete this entire poll? This will delete all questions, choices, and responses. This action cannot be undone.',
            confirmText: 'Delete Poll',
            onConfirm: async () => {
                try {
                    const response = await fetchWithAuth(`/api/polls/${pollId}/`, {
                        method: 'DELETE',
                        headers: { 'X-CSRFToken': csrfToken },
                    });

                    if (!response.ok) throw new Error('Failed to delete poll');

                    toast.showSuccess('Success', 'Poll deleted');
                    navigate('/pollsPage');
                } catch (error) {
                    toast.showError('Error', 'Failed to delete poll');
                }
            }
        });
    };

    const handleSavePrologue = async () => {
        try {
            setSavingPrologue(true);
            const response = await fetchWithAuth(`/api/polls/${pollId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({
                    prologue,
                    prologue_es: prologueEs || null,
                    public_title: publicTitle || null,
                    public_title_es: publicTitleEs || null,
                }),
            });

            if (!response.ok) throw new Error('Failed to save');

            const updatedPoll = await response.json();
            setPoll(updatedPoll);
            toast.showSuccess('Saved', 'Survey introduction updated');
        } catch (error) {
            toast.showError('Error', 'Failed to save introduction text');
        } finally {
            setSavingPrologue(false);
        }
    };

    if (loading) {
        return (
            <Box minH="100vh" bg="background.shade2" display="flex" alignItems="center" justifyContent="center">
                <LoadingSpinner />
            </Box>
        );
    }

    if (!poll) {
        return (
            <Box minH="100vh" bg="background.shade2" p={8}>
                <Text>Poll not found</Text>
            </Box>
        );
    }

    return (
        <Box minH="100vh" bg="background.shade2">
            <ConfirmDialogComponent />
            <PreflightModal
                isOpen={isPreflightOpen}
                onClose={onPreflightClose}
                pollId={pollId}
                onProceed={(data) => setPoll(data.poll)}
            />

            <Box maxW="800px" mx="auto" px={4} py={8}>
                <PageHeader
                    title="Poll Settings"
                    breadcrumbs={[
                        { label: 'Home', path: '/' },
                        { label: 'Polls', path: '/pollsPage' },
                        { label: poll.title, path: `/polls/${pollId}/edit` },
                        { label: 'Settings' }
                    ]}
                    subtitle={
                        <HStack>
                            <Text fontSize="sm" color="gray.600">Status:</Text>
                            <Badge colorScheme={getStateBadgeColor(poll.state)} fontSize="sm">
                                {poll.state.toUpperCase()}
                            </Badge>
                            <Text fontSize="sm" color="gray.500">
                                ({questionCount} question{questionCount !== 1 ? 's' : ''})
                            </Text>
                        </HStack>
                    }
                />

                {/* Quick Actions */}
                <Box bg="white" borderRadius="md" border="1px solid" borderColor="border.default" p={6} mb={6}>
                    <Heading as="h3" size="md" mb={4}>Quick Actions</Heading>

                    <VStack align="stretch" spacing={4}>
                        <HStack justify="space-between">
                            <Box>
                                <Text fontWeight="medium">Edit Questions</Text>
                                <Text fontSize="sm" color="gray.600">Add, edit, or reorder questions and choices</Text>
                            </Box>
                            <Button
                                leftIcon={<EditIcon />}
                                as={ReactRouterLink}
                                to={`/polls/${pollId}/edit`}
                            >
                                Edit Poll
                            </Button>
                        </HStack>

                        <Divider />

                        <HStack justify="space-between">
                            <Box>
                                <Text fontWeight="medium">Preview Survey</Text>
                                <Text fontSize="sm" color="gray.600">See how the survey looks to respondents</Text>
                            </Box>
                            <Button
                                leftIcon={<ViewIcon />}
                                onClick={() => window.open(`/preview-poll/${pollId}`, '_blank', 'width=500,height=900,scrollbars=yes')}
                                isDisabled={questionCount === 0}
                            >
                                Preview
                            </Button>
                        </HStack>
                    </VStack>
                </Box>

                {/* Poll State Management */}
                <Box bg="white" borderRadius="md" border="1px solid" borderColor="border.default" p={6} mb={6}>
                    <Heading as="h3" size="md" mb={4}>Poll Status</Heading>

                    {poll.state === 'draft' && (
                        <VStack align="stretch" spacing={4}>
                            <Alert status="info" borderRadius="md">
                                <AlertIcon />
                                This poll is in draft mode. Configure an audience and stage it when ready.
                            </Alert>

                            <HStack justify="space-between">
                                <Box>
                                    <Text fontWeight="medium">Stage Poll</Text>
                                    <Text fontSize="sm" color="gray.600">Configure audience, dates, and invitation text</Text>
                                </Box>
                                <Button
                                    as={ReactRouterLink}
                                    to="/stagingpoll"
                                    state={{ selectedPollId: pollId }}
                                    colorScheme="blue"
                                    isDisabled={questionCount === 0}
                                >
                                    Stage Poll
                                </Button>
                            </HStack>
                        </VStack>
                    )}

                    {poll.state === 'staged' && (
                        <VStack align="stretch" spacing={4}>
                            <Alert status="warning" borderRadius="md">
                                <AlertIcon />
                                This poll is staged and ready to go live.
                            </Alert>

                            <HStack justify="space-between">
                                <Box>
                                    <Text fontWeight="medium">Set Live</Text>
                                    <Text fontSize="sm" color="gray.600">Begin sending invitations and collecting responses</Text>
                                </Box>
                                <Button onClick={handleSetPollLive} colorScheme="green">
                                    Set Live
                                </Button>
                            </HStack>
                        </VStack>
                    )}

                    {poll.state === 'live' && (
                        <VStack align="stretch" spacing={4}>
                            <Alert status="success" borderRadius="md">
                                <AlertIcon />
                                This poll is live and collecting responses.
                            </Alert>

                            <HStack justify="space-between">
                                <Box>
                                    <Text fontWeight="medium">Live Dashboard</Text>
                                    <Text fontSize="sm" color="gray.600">Monitor responses and send invitations</Text>
                                </Box>
                                <Button
                                    as={ReactRouterLink}
                                    to={`/live-poll/${pollId}`}
                                    colorScheme="blue"
                                >
                                    Open Dashboard
                                </Button>
                            </HStack>

                            <Divider />

                            <HStack justify="space-between">
                                <Box>
                                    <Text fontWeight="medium">Analytics</Text>
                                    <Text fontSize="sm" color="gray.600">View results, crosstabs, and export data</Text>
                                </Box>
                                <Button
                                    as={ReactRouterLink}
                                    to={`/analytics/${pollId}`}
                                    colorScheme="purple"
                                >
                                    View Analytics
                                </Button>
                            </HStack>

                            <Divider />

                            <HStack justify="space-between">
                                <Box>
                                    <Text fontWeight="medium">Complete Poll</Text>
                                    <Text fontSize="sm" color="gray.600">Close the poll and stop accepting responses</Text>
                                </Box>
                                <Button onClick={handleCompletePoll} colorScheme="orange">
                                    Complete Poll
                                </Button>
                            </HStack>
                        </VStack>
                    )}

                    {poll.state === 'closed' && (
                        <VStack align="stretch" spacing={4}>
                            <Alert status="info" borderRadius="md">
                                <AlertIcon />
                                This poll is closed and no longer accepting responses.
                            </Alert>

                            <HStack justify="space-between">
                                <Box>
                                    <Text fontWeight="medium">View Analytics</Text>
                                    <Text fontSize="sm" color="gray.600">Review final results and export data</Text>
                                </Box>
                                <Button
                                    as={ReactRouterLink}
                                    to={`/analytics/${pollId}`}
                                    colorScheme="purple"
                                >
                                    View Analytics
                                </Button>
                            </HStack>
                        </VStack>
                    )}
                </Box>

                {/* Survey Introduction */}
                <Box bg="white" borderRadius="md" border="1px solid" borderColor="border.default" p={6} mb={6}>
                    <Heading as="h3" size="md" mb={2}>Survey Introduction</Heading>
                    <Text fontSize="sm" color="gray.600" mb={4}>
                        This is what respondents see on the welcome screen before the survey begins.
                    </Text>

                    <VStack align="stretch" spacing={4}>
                        <FormControl>
                            <FormLabel>Public-Facing Title</FormLabel>
                            <Input
                                value={publicTitle}
                                onChange={(e) => setPublicTitle(e.target.value)}
                                placeholder="e.g. 'Raise Your Voice' — leave blank to use poll title"
                            />
                            <FormHelperText>
                                Shown as the heading on the survey intro screen. If blank, uses "{poll.title}".
                            </FormHelperText>
                        </FormControl>

                        <FormControl>
                            <FormLabel color="gray.500">Public-Facing Title (Spanish)</FormLabel>
                            <Input
                                value={publicTitleEs}
                                onChange={(e) => setPublicTitleEs(e.target.value)}
                                placeholder="Optional Spanish title"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Introduction Text</FormLabel>
                            <Textarea
                                value={prologue}
                                onChange={(e) => setPrologue(e.target.value)}
                                placeholder="Thank you for participating in this survey. Your responses will help us understand..."
                                rows={4}
                            />
                            <FormHelperText>
                                Leave blank to show only the title and question count.
                            </FormHelperText>
                        </FormControl>

                        <FormControl>
                            <FormLabel color="gray.500">Introduction Text (Spanish)</FormLabel>
                            <Textarea
                                value={prologueEs}
                                onChange={(e) => setPrologueEs(e.target.value)}
                                placeholder="Gracias por participar en esta encuesta..."
                                rows={4}
                            />
                        </FormControl>

                        <Button
                            onClick={handleSavePrologue}
                            isLoading={savingPrologue}
                            loadingText="Saving..."
                            colorScheme="blue"
                            size="sm"
                            isDisabled={
                                prologue === (poll.prologue || '') &&
                                prologueEs === (poll.prologue_es || '') &&
                                publicTitle === (poll.public_title || '') &&
                                publicTitleEs === (poll.public_title_es || '')
                            }
                        >
                            Save Introduction
                        </Button>
                    </VStack>
                </Box>

                {/* Survey Layout */}
                <Box bg="white" borderRadius="md" border="1px solid" borderColor="border.default" p={6} mb={6}>
                    <Heading as="h3" size="md" mb={2}>Survey Layout</Heading>
                    <Text fontSize="sm" color="gray.600" mb={4}>
                        Organize questions into pages and add instructional text blocks.
                    </Text>
                    <SurveyPageDesigner pollId={pollId} />
                </Box>

                {/* Danger Zone */}
                <Box bg="white" borderRadius="md" border="1px solid" borderColor="red.200" p={6}>
                    <Heading as="h3" size="md" color="red.600" mb={4}>Danger Zone</Heading>

                    <HStack justify="space-between">
                        <Box>
                            <Text fontWeight="medium">Delete Poll</Text>
                            <Text fontSize="sm" color="gray.600">
                                Permanently delete this poll and all associated data
                            </Text>
                        </Box>
                        <Button
                            leftIcon={<DeleteIcon />}
                            colorScheme="red"
                            variant="outline"
                            onClick={handleDeletePoll}
                        >
                            Delete Poll
                        </Button>
                    </HStack>
                </Box>
            </Box>
        </Box>
    );
}

export default PollSettings;
